import React from 'react';
import { Link } from 'react-router-dom';
import {
  GitFork,
  Plus,
  CheckCircle,
  AlertCircle,
  XCircle,
  Loader2,
  ArrowUp,
  ArrowDown,
  ChevronRight,
} from 'lucide-react';
import { StatusBadge } from './StatusBadge';
import type { ConnectedRepository } from '../../types/dashboard';

interface RepositoryListSectionProps {
  // Current page from the server (already sorted/filtered server-side).
  repositories: ConnectedRepository[];
  // Total rows matching the active filter — used to render "X of Y" and pagination.
  totalCount: number;
  // Account-wide counts per status, computed server-side so chips show the
  // full picture even when only one page is loaded.
  statusCounts: { all: number; analyzing: number; error: number; issues: number; healthy: number };
  filterStatus: string;
  setFilterStatus: (value: string) => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  sortOrder: 'asc' | 'desc';
  setSortOrder: (value: 'asc' | 'desc' | ((prev: 'asc' | 'desc') => 'asc' | 'desc')) => void;
  currentPage: number;
  setCurrentPage: (page: number | ((prev: number) => number)) => void;
  totalDashboardPages: number;
  ITEMS_PER_PAGE: number;
}

export const RepositoryListSection: React.FC<RepositoryListSectionProps> = ({
  repositories,
  totalCount,
  statusCounts,
  filterStatus,
  setFilterStatus,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  currentPage,
  setCurrentPage,
  totalDashboardPages,
  ITEMS_PER_PAGE,
}) => {
  const hasRepositories = statusCounts.all > 0;

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700"
      data-guide="repositories"
    >
      {/* Header + inline controls */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-3 mr-auto">
            <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30">
              <GitFork className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Connected Repositories
            </h2>
            {statusCounts.all > 0 && (
              <span className="text-sm text-gray-400 dark:text-gray-500 tabular-nums">
                {totalCount !== statusCounts.all
                  ? `${totalCount} of ${statusCounts.all}`
                  : statusCounts.all}
              </span>
            )}
          </div>

          {statusCounts.all > 0 && (
            <>
              {/* Status pill group */}
              <div className="flex items-center gap-0.5 p-1 bg-gray-100 dark:bg-gray-700/60 rounded-lg">
                {(
                  [
                    {
                      value: 'all',
                      label: 'All',
                      icon: null,
                      active:
                        'bg-white dark:bg-gray-600 text-gray-800 dark:text-gray-100 shadow-sm',
                    },
                    {
                      value: 'healthy',
                      label: 'Healthy',
                      icon: CheckCircle,
                      active:
                        'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400',
                    },
                    {
                      value: 'issues',
                      label: 'Issues',
                      icon: AlertCircle,
                      active:
                        'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400',
                    },
                    {
                      value: 'error',
                      label: 'Error',
                      icon: XCircle,
                      active: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400',
                    },
                    {
                      value: 'analyzing',
                      label: 'Analyzing',
                      icon: Loader2,
                      active: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400',
                    },
                  ] as const
                ).map(({ value, label, icon: Icon, active }) => (
                  <button
                    key={value}
                    onClick={() => setFilterStatus(value)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                      filterStatus === value
                        ? active
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                    }`}
                  >
                    {Icon && (
                      <Icon
                        className={`w-3.5 h-3.5 ${value === 'analyzing' && filterStatus === 'analyzing' ? 'animate-spin' : ''}`}
                      />
                    )}
                    {label}
                  </button>
                ))}
              </div>

              {filterStatus !== 'all' && (
                <button
                  onClick={() => setFilterStatus('all')}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  title="Clear all filters"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}

              <div className="flex items-center gap-1">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-auto px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                  aria-label="Sort by"
                >
                  <option value="lastAnalysis">Last analysis</option>
                  <option value="devScore">Dev score</option>
                  <option value="maintenanceScore">Drift score</option>
                  <option value="name">Name</option>
                </select>
                <button
                  onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                  className="p-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 hover:border-teal-400 dark:hover:border-teal-500 transition-colors"
                  aria-label={
                    sortOrder === 'asc'
                      ? 'Sort ascending — click for descending'
                      : 'Sort descending — click for ascending'
                  }
                  title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortOrder === 'asc' ? (
                    <ArrowUp className="w-3.5 h-3.5" />
                  ) : (
                    <ArrowDown className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-6">
        {!hasRepositories ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center mb-4">
              <GitFork className="w-8 h-8 text-teal-600 dark:text-teal-400" />
            </div>
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
              Ready to improve your code?
            </h3>
            <p className="text-sm mb-6 text-center max-w-md text-gray-600 dark:text-gray-400">
              Connect your first project and we'll help you make it better, faster, and more secure.
            </p>
            <Link to="/repositories" className="btn-primary inline-flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Connect Your First Project
            </Link>
          </div>
        ) : repositories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
              <GitFork className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
              No repositories match this filter
            </h3>
            <p className="text-sm mb-6 text-center max-w-md text-gray-600 dark:text-gray-400">
              Try a different status or clear the filter to see all {statusCounts.all} repositories.
            </p>
            {filterStatus !== 'all' && (
              <button
                onClick={() => setFilterStatus('all')}
                className="btn-secondary inline-flex items-center gap-2"
              >
                Clear filter
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {repositories.map((repo) => {
                const repoState = {
                  repository: {
                    ...repo,
                    provider: repo.provider,
                    platform: repo.platform || repo.provider,
                    source: repo.source,
                    fullName: repo.fullName || repo.name,
                    cloneUrl: repo.cloneUrl || repo.url,
                  },
                };

                return (
                  <Link
                    key={repo.id}
                    to={`/repository/${repo.id}`}
                    state={repoState}
                    className="block bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 cursor-pointer transition-colors duration-200 hover:border-teal-500 focus:ring-2 focus:ring-teal-500 focus:outline-hidden no-underline text-inherit"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {repo.fullName || repo.name}
                          </h3>
                          <StatusBadge status={repo.status} />
                        </div>
                        {repo.url && (
                          <p className="text-sm mb-3 truncate text-gray-600 dark:text-gray-400">
                            {repo.url}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                          <span>
                            Last accessed:{' '}
                            {repo.lastAnalysis ? repo.lastAnalysis.toLocaleDateString() : 'Never'}
                          </span>
                          {repo.analysisCount > 0 && (
                            <span>
                              {repo.analysisCount}{' '}
                              {repo.analysisCount === 1 ? 'analysis' : 'analyses'}
                            </span>
                          )}
                          {repo.devScore !== null && (
                            <span className="flex items-center gap-1">
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  repo.devScore >= 80
                                    ? 'bg-green-500'
                                    : repo.devScore >= 60
                                      ? 'bg-yellow-500'
                                      : 'bg-red-500'
                                }`}
                              />
                              SDLC: {repo.devScore}/100
                            </span>
                          )}
                          {repo.maintenanceScore !== null && (
                            <span className="flex items-center gap-1">
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${
                                  repo.maintenanceScore >= 80
                                    ? 'bg-green-500'
                                    : repo.maintenanceScore >= 60
                                      ? 'bg-yellow-500'
                                      : 'bg-red-500'
                                }`}
                              />
                              Drift: {repo.maintenanceScore}/100
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-6 h-6 text-gray-400 shrink-0 ml-4 self-center" />
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Pagination */}
            {totalDashboardPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{' '}
                  {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} projects
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="btn-ghost px-3 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalDashboardPages) }, (_, i) => {
                      let pageNum;
                      if (totalDashboardPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalDashboardPages - 2) {
                        pageNum = totalDashboardPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1.5 text-sm rounded ${
                            currentPage === pageNum ? 'bg-teal-500 text-white' : 'btn-ghost'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalDashboardPages, prev + 1))
                    }
                    disabled={currentPage === totalDashboardPages}
                    className="btn-ghost px-3 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
